import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { extractApiError } from '~/utils/extract-api-error';

import type { ArchiveBatchSummary } from '../types/list-archive.types';
import { describeArchiveRefusal, type ArchiveRefusal } from '../utils/archive-error';

/**
 * Toast copy shared by every archive mutation. Success offers an Undo action on
 * archive (unarchive is an exact, lossless inverse); failure maps the contract's
 * refusal codes to a user-facing sentence instead of the bare status word. The
 * two refusals that have an action (a live job, an in-flight experiment) are
 * NOT toasted here — the caller opens a Stop dialog for them instead.
 */
export function useArchiveFeedback() {
  const { t } = useTranslation('common');

  /** `archived` = the state the row is now in; `onUndo` re-toggles it back. */
  const notifySuccess = (archived: boolean, onUndo?: () => void) => {
    if (!archived) {
      toast.success(t('listArchive.toast.unarchived'));
      return;
    }
    toast.success(
      t('listArchive.toast.archived'),
      onUndo ? { action: { label: t('listArchive.toast.undo'), onClick: onUndo } } : undefined,
    );
  };

  /** Toasts every refusal that has no follow-up action. Returns the refusal so
   *  the caller can still branch on the actionable kinds. */
  const notifyError = (err: unknown): ArchiveRefusal => {
    const refusal = describeArchiveRefusal(err);
    switch (refusal.kind) {
      case 'still-live':
        // OPEN has a Stop action (dialog, owned by the caller); only the
        // "couldn't verify" case is a plain toast — and it must not claim the
        // run is live.
        if (refusal.liveness !== 'OPEN') toast.warning(t('listArchive.toast.unverified'));
        break;
      case 'delivering':
        toast.info(t('listArchive.toast.delivering'));
        break;
      case 'experiment-in-flight':
      case 'state-changed':
        // Caller-owned: Stop dialog / silent retry.
        break;
      case 'not-found':
        toast.info(t('listArchive.toast.notFound'));
        break;
      case 'no-subsidiary':
        toast.warning(t('listArchive.toast.noSubsidiary'));
        break;
      default:
        toast.error(extractApiError(err, t('listArchive.toast.error')));
    }
    return refusal;
  };

  /** One toast for a whole batch, built from the server's `summary` — e.g.
   *  "84 archived · 2 already were · 3 still running · 1 not found". Zero
   *  buckets are omitted; a batch that did nothing but refuse reads as a warning. */
  const notifyBatch = (summary: ArchiveBatchSummary, archived: boolean) => {
    const parts: string[] = [];
    if (summary.updated > 0) {
      parts.push(
        t(archived ? 'listArchive.bulk.summary.archived' : 'listArchive.bulk.summary.restored', {
          count: summary.updated,
        }),
      );
    }
    if (summary.unchanged > 0) {
      parts.push(t('listArchive.bulk.summary.unchanged', { count: summary.unchanged }));
    }
    if (summary.refused > 0) {
      parts.push(t('listArchive.bulk.summary.refused', { count: summary.refused }));
    }
    if (summary.notFound > 0) {
      parts.push(t('listArchive.bulk.summary.notFound', { count: summary.notFound }));
    }
    if (summary.errored > 0) {
      parts.push(t('listArchive.bulk.summary.errored', { count: summary.errored }));
    }
    const text = parts.join(' · ') || t('listArchive.bulk.summary.nothing');
    const didWork = summary.updated > 0 || summary.unchanged > 0;
    if (didWork) toast.success(text);
    else toast.warning(text);
  };

  return { notifySuccess, notifyError, notifyBatch };
}
